import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { appendToRmaApiUrl } from '@rma/generic/ng/util';
import { ProfileTheme } from '@rma/public/generic/domain/types';
import { catchError, map, Observable, of } from 'rxjs';

// Optional throughout: the endpoint answers for every requested code, branded or not.
interface AgencyBranding {
  agencyCode?: string;
  derivedBranding?: ProfileTheme;
}

@Injectable({
  providedIn: 'root',
})
export class AgencyThemeService {
  private readonly httpClient = inject(HttpClient);
  private readonly rmaApiUrl = inject(appendToRmaApiUrl);

  /**
   * Many agencies in one request. A per-code route also exists; calling it in a loop is the N+1 this
   * route was added to avoid.
   */
  public themes(agencyCodes: string[]): Observable<ReadonlyMap<string, ProfileTheme>> {
    if (!agencyCodes.length) {
      return of(new Map<string, ProfileTheme>());
    }

    return this.httpClient.get<AgencyBranding[] | null>(this.rmaApiUrl('Agencies/Theme'), { params: { agencyCodes } }).pipe(
      map((agencies) => {
        const themes = new Map<string, ProfileTheme>();

        for (const agency of agencies ?? []) {
          if (agency.agencyCode && agency.derivedBranding) {
            themes.set(agency.agencyCode, agency.derivedBranding);
          }
        }

        return themes;
      }),
      // Branding is decoration: failing it unbrands the tiles rather than failing the tray.
      catchError(() => of(new Map<string, ProfileTheme>())),
    );
  }
}
